/**
 * Feature-flag hook + gated CTA components.
 *
 * Fetches /api/flags once at app boot, caches per-session in `localStorage`,
 * and exposes a `useFlag(id)` hook. Backend is source of truth — flags
 * refresh on every full page load.
 */
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const CACHE_KEY = "orixa_flags_v1";
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const FlagsContext = createContext({ flags: {}, loaded: false });

export function FlagsProvider({ children }) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (Date.now() - cached.at < CACHE_TTL_MS) {
          return { flags: cached.flags || {}, loaded: true };
        }
      }
    } catch { /* noop */ }
    return { flags: {}, loaded: false };
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/flags");
        if (cancelled) return;
        const flags = data.flags || {};
        setState({ flags, loaded: true });
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ flags, at: Date.now() })); } catch { /* noop */ }
      } catch {
        if (!cancelled) setState((s) => ({ ...s, loaded: true }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return <FlagsContext.Provider value={state}>{children}</FlagsContext.Provider>;
}

/** useFlag("subscriptions") → { state, loaded, is }
 *  state: "disabled" | "waitlist" | "early_access" | "paid" | "free" | "unknown"
 *  is(...): convenience — is("paid") returns boolean */
export function useFlag(id, fallback = "unknown") {
  const ctx = useContext(FlagsContext);
  const entry = ctx.flags?.[id];
  return useMemo(() => ({
    loaded: ctx.loaded,
    state: entry?.state || fallback,
    label: entry?.label,
    is: (...states) => states.includes(entry?.state),
  }), [ctx.loaded, entry, fallback]);
}
